import { useQuery } from '@tanstack/react-query';
import getFormVariantValues from '../lib/getFormVariantValues';
import useCheckoutState from './useCheckoutState';
import api from '../api';
import { prorationCalculationQueryKey } from '../queryKeys';
import { checkHasBillingCycleAnchorConfig } from '../lib/checkHasBillingCycleAnchorConfig';

export function useCalculateProratedAmountQuery(
  {
    sellerId,
    pageId,
    variants,
    quantity,
    selectedPaymentMethod,
    couponId,
    dynamicPrice,
    billingCycleAnchorConfigEnabled,
    dayOfMonth,
    month,
  },
  enabled,
  { initialData },
) {
  const queryKey = prorationCalculationQueryKey({
    sellerId,
    pageId,
    variants,
    quantity,
    selectedPaymentMethod,
    dynamicPrice,
    couponId,
    billingCycleAnchorConfigEnabled,
    dayOfMonth,
    month,
  });

  return useQuery({
    queryKey,
    queryFn: () =>
      api.calculateProration(
        {
          variants,
          quantity,
          selectedPaymentMethod,
          couponId,
        },
        sellerId,
        pageId,
      ),
    enabled: enabled && !!pageId && !!sellerId,
    retry: 3,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    ...(initialData && { initialData }),
  });
}

export default function useProration() {
  const { page, seller, form, product, coupon, selectedPaymentMethod, dynamicPrice } =
    useCheckoutState();

  if (!product) {
    return {
      proration: {
        amount: 0,
        nextBillingDate: null,
      },
      isLoadingProration: false,
      isRefetchingProration: false,
      hasProration: false,
    };
  }

  const hasBillingCycleAnchorConfig = checkHasBillingCycleAnchorConfig(product);
  const hasProration = hasBillingCycleAnchorConfig; // proration calculations are already included in tax calculations

  const billingCycleAnchorConfigEnabled = product.billingCycleAnchorConfig?.enabled;
  const dayOfMonth = product.billingCycleAnchorConfig?.dayOfMonth;
  const month = product.billingCycleAnchorConfig?.month;

  const variants = getFormVariantValues(product.variants, form.variants);

  const { data, isLoading, isRefetching } = useCalculateProratedAmountQuery(
    {
      sellerId: seller?._id,
      pageId: page?._id,
      variants,
      quantity: form.quantity,
      selectedPaymentMethod,
      couponId: coupon?._id,
      dynamicPrice,
      billingCycleAnchorConfigEnabled,
      dayOfMonth,
      month,
    },
    hasProration,
    {
      initialData: product.proration,
    },
  );

  return {
    proration: {
      amount: data?.amount || 0,
      nextBillingDate: data?.nextBillingDate,
    },
    isLoadingProration: isLoading,
    isRefetchingProration: isRefetching,
    hasProration,
  };
}
